import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  BehaviorSubject,
  combineLatest,
  map,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { TuiAlertService } from '@taiga-ui/core';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import {
  AssignDriverRequest,
  AssignDriverResponse,
  AssignDriverServiceInfo,
  DriverVM,
} from 'src/app/models/driver/driver.model';
import { ActiveDriver } from 'src/app/models/map-drivers/map-drivers.model';
import { MapaServiceService } from 'src/app/services/mapa/mapa-service.service';
import { DriverService } from 'src/app/services/driver/driver.service';
import { HistoryServicesService } from 'src/app/services/history-services/history-services.service';
import { TranslateService } from '@ngx-translate/core';

enum OptionView {
  Closest = 'closest',
  Active = 'active',
}

@Component({
  selector: 'app-modal-assign-driver',
  templateUrl: './modal-assign-driver.component.html',
  styleUrls: ['./modal-assign-driver.component.css'],
})
export class ModalAssignDriverComponent implements OnInit, OnDestroy {
  /* --------------------- Inputs que inyecta el padre ------------------ */

  @Input() orderHash!: string;
  @Input() zoneId!: string;
  @Input() nodeId!: string;
  @Input() filter: 'all' | 'sweeper' = 'all';
  @Input() pickupSeconds!: number;
  @Input() serviceInfo!: AssignDriverServiceInfo;

  @Output() driverAssigned = new EventEmitter<void>();

  readonly assignForm: FormGroup = this.fb.group({
    driverId: ['', Validators.required],
  });
  readonly searchControl = new FormControl('');

  readonly search$ = new BehaviorSubject<string>('');
  readonly optionView$ = new BehaviorSubject<OptionView>(OptionView.Active);
  private readonly destroy$ = new Subject<void>();
  /** indicador de spinner global */
  readonly loading$ = new BehaviorSubject<boolean>(true);
  readonly loadingAssign$ = new BehaviorSubject<boolean>(false);

  readonly OptionView = OptionView;

  activeDrivers$!: BehaviorSubject<ActiveDriver[]>;
  visibleDrivers$!: BehaviorSubject<DriverVM[]>;
  counters$!: BehaviorSubject<{ active: number }>;

  constructor(
    private readonly fb: FormBuilder,
    private readonly mapService: MapaServiceService,
    private readonly alert: TuiAlertService,
    private readonly driverService: DriverService,
    private readonly historyService: HistoryServicesService,
    private readonly offcanvas: NgbActiveOffcanvas,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.initActiveDriversStream();
    this.initVisibleDriversStream();
    this.initCountersStream();
    this.initSearchListener();
    this.activeDrivers$.pipe(takeUntil(this.destroy$)).subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initActiveDriversStream(): void {
    this.activeDrivers$ = new BehaviorSubject<ActiveDriver[]>([]);
    this.driverService
      .getActiveDriversByRegion(this.zoneId)
      .pipe(
        map((dtos) =>
          dtos.map(
            (dto) =>
              ({
                driverId: dto.driverGuid,
                general_info: { fullName: dto.driverDisplayName },
              } as ActiveDriver)
          )
        ),
        tap((drivers) => {
          this.activeDrivers$.next(drivers);
          this.loading$.next(false);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private initVisibleDriversStream(): void {
    this.visibleDrivers$ = new BehaviorSubject<DriverVM[]>([]);

    combineLatest([this.activeDrivers$, this.search$])
      .pipe(
        map(([active, term]) =>
          active
            .filter((d) =>
              d.general_info?.fullName
                .toLowerCase()
                .includes(term.toLowerCase())
            )
            .map((d) => ({
              driverId: d.driverId,
              name: d.general_info.fullName,
            }))
            .sort((a, b) => a.name.localeCompare(b.name))
        ),
        tap((vm) => this.visibleDrivers$.next(vm)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private initCountersStream(): void {
    this.counters$ = new BehaviorSubject<{ active: number }>({ active: 0 });

    this.activeDrivers$
      .pipe(
        map((active) => ({ active: active.length })),
        tap((c) => this.counters$.next(c)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private initSearchListener(): void {
    this.searchControl.valueChanges
      .pipe(
        tap((value) => this.search$.next((value ?? '').trim().toLowerCase())),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  selectDriver(id: string): void {
    this.assignForm.setValue({ driverId: id });
  }

  close(): void {
    this.offcanvas.dismiss();
  }

  assign(): void {
    if (this.assignForm.invalid) {
      this.alert
        .open(this.translate.instant('modal-assign-driver.select_driver'), {
          label: this.translate.instant(
            'modal-assign-driver.select_driver_title'
          ),
          status: 'warning',
          autoClose: true,
        })
        .subscribe();
      return;
    }

    this.loadingAssign$.next(true);

    const req: AssignDriverRequest = {
      driverId: this.assignForm.value.driverId,
      isSweeperAssignation: this.filter !== 'all',
      nodeId: this.nodeId,
      orderHash: this.orderHash,
    };

    this.mapService
      .assignDriver(req)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => this.handleAssignResponse(res));
  }

  private handleAssignResponse(res: AssignDriverResponse): void {
    if (!res.wasSuccessful) {
      this.loadingAssign$.next(false);

      this.alert
        .open(this.translate.instant('modal-assign-driver.assign_error'), {
          label: this.translate.instant(
            'modal-assign-driver.assign_error_title'
          ),
          status: 'error',
          autoClose: true,
        })
        .subscribe();
      return;
    }

    this.alert
      .open(this.translate.instant('modal-assign-driver.assign_success'), {
        label: this.translate.instant(
          'modal-assign-driver.assign_success_title'
        ),
        status: 'success',
        autoClose: true,
      })
      .subscribe();

    this.loadingAssign$.next(false);
    this.driverAssigned.emit();
    this.offcanvas.close('ASSIGNED');
  }

  trackByDriverId(_: number, d: DriverVM): string {
    return d.driverId;
  }

  getPickupHour(): string {
    const date = new Date(this.pickupSeconds * 1000);
    const hh = date.getHours().toString().padStart(2, '0');
    const mm = date.getMinutes().toString().padStart(2, '0');
    return `${hh}:${mm}`;
  }

  get serviceTypeLabel(): string {
    const key = 'common.services.' + this.serviceInfo.mainServiceTypeName;
    const translated = this.translate.instant(key);

    return translated === key
      ? this.serviceInfo.mainServiceTypeName
      : translated;
  }

  getCurrentHourAndMinute(
    includeSeconds: boolean = false,
    separator: string = ':'
  ): string {
    const now = new Date();

    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');

    let timeString = `${hours}${separator}${minutes}`;

    if (includeSeconds) {
      const seconds = now.getSeconds().toString().padStart(2, '0');
      timeString += `${separator}${seconds}`;
    }

    return timeString;
  }

  journeyTime(km: number): string {
    const hrs = Math.floor(km / 70);
    const min = Math.round((km / 70 - hrs) * 60);
    return `${hrs}h ${min}m`;
  }

  translateKey(key: string): string {
    return this.historyService.translateServiceKey(key);
  }

  getTranslation(key: string): string {
    if (this.historyService.traducciones.hasOwnProperty(key)) {
      return this.historyService.traducciones[key];
    }

    return key;
  }
}
